'use client';

import * as React from 'react';
import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import styles from './Checkbox.module.css';
import clsx from 'clsx';

export interface CheckboxProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  /** Text, or inline content such as a link (wrapped as one line of text). */
  label?: React.ReactNode;
  description?: string;
  error?: string;
  /**
   * Flags the checkbox as invalid (red border) without rendering a message
   * below it — for cases where a single shared error message is shown
   * elsewhere (e.g. once below a group of checkboxes) instead of repeating
   * the same text under every item.
   */
  invalid?: boolean;
  name?: string;
  value?: string;
  indeterminate?: boolean;
  className?: string;
  /**
   * Position of the label relative to the checkbox
   * - right (default): checkbox, then label
   * - left: label, then checkbox
   */
  labelPosition?: 'left' | 'right';
}

export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  (
    {
      checked,
      defaultChecked,
      onChange,
      disabled = false,
      label,
      description,
      error,
      invalid = false,
      name,
      value,
      indeterminate = false,
      className,
      labelPosition = 'right',
    },
    ref
  ) => {
    return (
      <div className={clsx(styles.container, className)}>
        <label
          className={clsx(
            styles.label,
            labelPosition === 'left' && styles.labelLeft,
            disabled && styles.disabled
          )}
        >
          <BaseCheckbox.Root
            ref={ref}
            checked={checked}
            defaultChecked={defaultChecked}
            onCheckedChange={onChange}
            disabled={disabled}
            name={name}
            className={clsx(
              styles.checkbox,
              (error || invalid) && styles.error,
              indeterminate && styles.indeterminate
            )}
          >
            <BaseCheckbox.Indicator className={styles.indicator}>
              {indeterminate ? (
                <svg viewBox="0 0 12 12" fill="none" className={styles.icon}>
                  <path d="M2.5 6h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              ) : (
                <svg viewBox="0 0 12 12" fill="none" className={styles.icon}>
                  <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </BaseCheckbox.Indicator>
          </BaseCheckbox.Root>
          {label && (
            <span className={styles.labelText}>
              <span>{label}</span>
              {description && <span className={styles.description}>{description}</span>}
            </span>
          )}
        </label>
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
